import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { AuthService } from '../../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class SessionMonitorService implements OnDestroy {
  private checkInterval = 30000; // Check every 30 seconds
  private sessionCheckSubscription?: Subscription;
  private isCheckingSession = false;
  
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}
  
  startMonitoring(): void {
    // Don't start if already monitoring
    if (this.sessionCheckSubscription) {
      return;
    }
    
    console.log('Starting session monitoring...');
    
    // Check immediately
    this.checkSession();
    
    // Then check periodically
    this.sessionCheckSubscription = interval(this.checkInterval).subscribe(() => {
      this.checkSession();
    });
  }
  
  stopMonitoring(): void {
    if (this.sessionCheckSubscription) {
      console.log('Stopping session monitoring...');
      this.sessionCheckSubscription.unsubscribe();
      this.sessionCheckSubscription = undefined;
    }
  }
  
  private checkSession(): void {
    // Prevent concurrent checks
    if (this.isCheckingSession) {
      return;
    }
    
    // Don't check if we're on auth pages
    if (this.router.url.includes('/auth/')) {
      return;
    }
    
    this.isCheckingSession = true;
    
    try {
      const token = this.authService.getAccessToken();
      
      if (!token) {
        console.log('Session monitor: No token found');
        this.handleSessionExpired();
        return;
      }
      
      // Check if token is expired
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = payload.exp - now;

        console.log('Session monitor: Token check', {
          currentTime: now,
          tokenExpiry: payload.exp,
          timeUntilExpiry: timeUntilExpiry,
          expiryDate: new Date(payload.exp * 1000).toISOString()
        });

        if (timeUntilExpiry <= 0) {
          console.log('Session monitor: Token is expired - logging out');
          this.handleSessionExpired();
        } else if (timeUntilExpiry < 300) { // Less than 5 minutes
          console.log(`Session monitor: Token expires in ${timeUntilExpiry} seconds`);
          // Could implement a warning here
        }
      } catch (error) {
        console.error('Session monitor: Error parsing token', error);
        this.handleSessionExpired();
      }
    } finally {
      this.isCheckingSession = false;
    }
  }
  
  private handleSessionExpired(): void {
    // Stop monitoring to prevent multiple redirects
    this.stopMonitoring();
    
    // Store current URL
    const currentUrl = this.router.url;
    
    // Clear auth data
    this.authService.logout();
    
    // Navigate to login with message
    setTimeout(() => {
      if (!this.router.url.includes('/auth/login')) {
        this.router.navigate(['/auth/login'], {
          queryParams: {
            message: 'Your session has expired. Please login again.',
            returnUrl: currentUrl !== '/dashboard' ? currentUrl : undefined
          }
        });
      }
    }, 100);
  }
  
  ngOnDestroy(): void {
    this.stopMonitoring();
  }
}